import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "convex/react";
import {
  AlertTriangle,
  ArrowLeftRight,
  CheckCircle2,
  Compass,
  Loader2,
  Lock,
  Search,
} from "lucide-react";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import {
  extractScoresFromResult,
  PamiMatchingChart,
  type LigneMatchingPami,
  type TraitResult,
} from "@repo/results";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type {
  StatDimension,
  StatFourchette,
} from "../../../../convex/referentiel/agregation";
import {
  niveauxForcesQsort,
  seuilReferentsFiable,
} from "../../../../convex/referentiel/agregation";
import type {
  LigneComparaison,
  ResultatComparaison,
} from "../../../../convex/referentiel/comparer";
import {
  comparerFourchette,
  comparerQsort,
} from "../../../../convex/referentiel/comparer";
import { scoreDuNiveau } from "../../../../convex/referentiel/qsort";
import {
  construireRapportCulturel,
  interpreterCorrelation,
} from "../../../../convex/referentiel/rapport";
import {
  COULEUR_ZONE,
  formatR,
  libelleIndicateur,
  RapportMatchingCulturel,
} from "./RapportMatchingCulturel";
import { dateCourte } from "@repo/utils";

/** Une évaluation PAMI de la campagne, telle que renvoyée par
 *  `obtenirEvaluationsPamiParCampagne`. */
export type EvaluationPamiRow = {
  _id: Id<"evaluations">;
  parcoursId: Id<"parcoursEvaluations">;
  nomCandidat?: string;
  prenomCandidat?: string;
  scoresJson?: string;
};

/** Une passation fit-culturel, telle que renvoyée par
 *  `obtenirEvaluationsFitCulturelParCampagne`. Le NIVEAU est brut : le score est
 *  recalculé ici depuis la pyramide de la campagne (§1.3). */
export type EvaluationFitCulturelRow = {
  _id: Id<"evaluations">;
  parcoursId: Id<"parcoursEvaluations">;
  nomCandidat?: string;
  prenomCandidat?: string;
  dateFin?: number;
  niveaux: { code: string; niveau: number }[];
};

type Props = {
  campagneId: Id<"campagnes">;
  /** Référentiel contre lequel comparer — celui du test sélectionné. */
  cible: "pami" | "pc-fit";
  /** Tient lieu de poste visé dans le rapport de matching culturel. */
  campagneNom?: string;
  evaluationsPami: EvaluationPamiRow[] | undefined;
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined;
  /**
   * Jeton d'auth et URL du déploiement de l'app appelante, pour proposer le
   * rapport PDF remis au candidat. Absents = pas de bouton : ce paquet ne lit
   * aucun `process.env`, qui serait vide hors du bundle de l'app.
   */
  token?: string | null;
  convexUrl?: string;
};

// Diverging blue ↔ red avec point neutre gris : les deux pôles se lisent comme
// opposés (sous la cible / au-dessus de la cible), le milieu comme « rien ».
// Jamais la couleur seule — chaque ligne porte aussi son libellé de statut.
const COULEUR_STATUT: Record<LigneComparaison["statut"], string> = {
  "sous-la-cible": "var(--chart-region-2)",
  "au-dessus-de-la-cible": "var(--chart-region-4)",
  "dans-la-cible": "var(--muted-foreground)",
  // Jamais atteint dans le tableau fit-culturel (Q-sort n'a pas de dimension
  // « sans zone ») — présent pour l'exhaustivité du type, partagé avec le PAMI.
  "sans-zone": "var(--muted-foreground)",
};

const LIBELLE_STATUT: Record<LigneComparaison["statut"], string> = {
  "sous-la-cible": "Sous la cible",
  "au-dessus-de-la-cible": "Au-dessus de la cible",
  "dans-la-cible": "Dans la cible",
  "sans-zone": "Pas d'attente particulière",
};

const arrondi1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Bande à tracer pour une ligne, quelle que soit l'échelle.
 *
 * En fourchette la bande EST la cible saisie. En Q-sort c'est le CRAN attendu :
 * `comparerQsort` ne tolère plus d'écart, la bande est donc le demi-cran de part
 * et d'autre du niveau du référentiel — ce qui fait que « marqueur dans la
 * bande » signifie exactement « même niveau », sans pouvoir contredire le statut.
 *
 * Un demi-cran de chaque côté et non `[niveau − 1, niveau]` comme les anneaux du
 * radar : sur une échelle linéaire, un niveau est un POINT, et une bande partant
 * du cran inférieur ferait tomber un candidat un cran trop bas pile sur son bord.
 */
function bandeCible(ligne: LigneComparaison): { min: number; max: number } {
  if (ligne.cible.kind === "fourchette") {
    return { min: ligne.cible.min, max: ligne.cible.max };
  }
  if (ligne.cible.kind === "score") {
    return { min: ligne.cible.valeur - 0.5, max: ligne.cible.valeur + 0.5 };
  }
  // "sans-zone" : jamais produit par `comparerQsort`, seule cible utilisée
  // dans cette table (fit-culturel). Le PAMI passe par `PamiMatchingChart`.
  return { min: ligne.valeurCandidat, max: ligne.valeurCandidat };
}

/**
 * Bande cible dessinée sur l'échelle, marqueur du candidat par-dessus.
 *
 * Sert les deux échelles : en fourchette la bande EST la cible `[min, max]` sur
 * 0-10 ; en Q-sort c'est le cran attendu par la pyramide finale, à un demi-cran
 * près, tracé sur l'échelle des scores de niveau. Dans les deux cas, « le
 * marqueur est dans la bande » signifie exactement « dans la cible ».
 */
function TrackCible({
  bande,
  valeurCandidat,
  echelle,
  statut,
}: {
  bande: { min: number; max: number };
  valeurCandidat: number;
  echelle: { min: number; max: number };
  statut: LigneComparaison["statut"];
}) {
  const amplitude = echelle.max - echelle.min || 1;
  const pct = (v: number) =>
    Math.max(0, Math.min(100, ((v - echelle.min) / amplitude) * 100));
  return (
    <div className="relative h-4 w-full min-w-[7rem] rounded-full bg-muted">
      <div
        className="absolute inset-y-0 rounded-full bg-foreground/15"
        style={{
          left: `${pct(bande.min)}%`,
          width: `${Math.max(1.5, pct(bande.max) - pct(bande.min))}%`,
        }}
      />
      {/* Marqueur candidat : cerné de 2px de surface pour rester lisible
          par-dessus la bande cible. */}
      <div
        className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card"
        style={{
          left: `${pct(valeurCandidat)}%`,
          backgroundColor: COULEUR_STATUT[statut],
        }}
      />
    </div>
  );
}

/** Barre divergente centrée sur 0 : à gauche sous la cible, à droite au-dessus. */
function BarreEcart({
  ligne,
  ecartMaxAffiche,
}: {
  ligne: LigneComparaison;
  ecartMaxAffiche: number;
}) {
  const largeur =
    ecartMaxAffiche > 0 ? (Math.abs(ligne.ecart) / ecartMaxAffiche) * 50 : 0;
  const versLaGauche = ligne.statut === "sous-la-cible";
  return (
    <div className="relative h-4 w-full min-w-[6rem]">
      <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
      {ligne.statut === "dans-la-cible" ? (
        <div className="absolute top-1/2 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground" />
      ) : (
        <div
          className="absolute inset-y-[3px] rounded-full"
          style={{
            [versLaGauche ? "right" : "left"]: "50%",
            width: `${Math.max(1, largeur)}%`,
            backgroundColor: COULEUR_STATUT[ligne.statut],
          }}
        />
      )}
    </div>
  );
}

/**
 * Score PAMI sur 100. Pas de palier d'interprétation : le matching y est le
 * décompte des niveaux atteints, une grandeur qui se lit telle quelle. Le
 * fit-culturel (r) se lit dans `RapportMatchingCulturel`.
 */
function JaugeMatching({
  score,
  fiabilite,
  rang,
  total,
}: {
  score: number;
  fiabilite: "faible" | "correcte";
  rang: number;
  total: number;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-end gap-3">
        <span className="text-4xl leading-none font-bold tabular-nums">
          {Math.round(score)}
        </span>
        <span className="text-muted-foreground pb-1 text-sm">/ 100</span>
        {fiabilite === "faible" && (
          <Badge variant="outline" className="mb-1">
            Fiabilité faible
          </Badge>
        )}
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.max(1, Math.min(100, score))}%` }}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Rang {rang} sur {total} dans cette campagne.
      </p>
    </div>
  );
}

function BlocDimensions({
  titre,
  description,
  icone,
  lignes,
  noteHexaco,
}: {
  titre: string;
  description: string;
  icone: ReactNode;
  lignes: LigneComparaison[];
  noteHexaco?: boolean;
}) {
  const aDesFacettes =
    noteHexaco && lignes.some((l) => l.categorie === "personnalite");
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {icone}
          {titre}
          <span className="text-muted-foreground text-sm font-normal">
            ({lignes.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground text-xs">{description}</p>
        {lignes.length === 0 ? (
          <p className="text-muted-foreground">Aucune dimension.</p>
        ) : (
          <ul className="space-y-1">
            {lignes.map((ligne) => (
              <li key={ligne.code} className="flex items-baseline gap-2">
                <span className="font-medium">{ligne.label}</span>
                {ligne.categorie === "personnalite" && (
                  <span className="text-muted-foreground text-xs">
                    · à explorer en entretien
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {aDesFacettes && (
          <p className="text-muted-foreground border-t pt-2 text-xs">
            Les facettes de personnalité décrivent un attendu du poste, pas une
            aptitude : un écart signale un point à explorer en entretien, il ne
            qualifie pas le candidat.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** Nombre de candidats affichés par défaut dans le classement. Au-delà, la
 *  lecture ligne à ligne n'apporte plus rien : le tableau sert à repérer le
 *  haut du classement, pas à parcourir toute la campagne. */
const TOP_CLASSEMENT = 5;

/**
 * Matching candidat ↔ référentiel entreprise (plan §8.1).
 *
 * Le score n'est JAMAIS stocké : il est recalculé à la lecture contre une
 * projection du référentiel. C'est ce qui garantit que tous les candidats de la
 * campagne sont comparés au même référentiel — et que le classement ci-dessous
 * est légitime (§4.1). Corollaire : tout score est rendu avec l'identité de la
 * version qui l'a produit.
 *
 * Le calcul tourne côté front (§5.5), comme les scores PAMI : `computePamiScores`
 * vit dans `@repo/utils`, dont Convex évite la dépendance.
 */
export function ReferentielMatchingTab({
  campagneId,
  cible,
  campagneNom,
  evaluationsPami,
  evaluationsFitCulturel,
  token,
  convexUrl,
}: Props) {
  const etat = useQuery(api.referentiel.campagne.obtenirEtatReferentiel, {
    campagneId,
    cible,
  });
  const [versionChoisie, setVersionChoisie] = useState<number | null>(null);
  const [toutAfficher, setToutAfficher] = useState(false);
  const [parcoursSelectionne, setParcoursSelectionne] = useState<string | null>(
    null,
  );

  const versions = etat?.historique ?? [];
  // Par défaut la DERNIÈRE version : c'est la seule contre laquelle tous les
  // candidats sont comparables entre eux.
  const version =
    versions.find((v) => v.version === versionChoisie) ??
    versions[versions.length - 1];

  // Mémoïsés : identité stable, sinon le classement serait recalculé à chaque
  // rendu (le moteur est pur, mais la comparaison de dépendances ne l'est pas).
  const echelleConfig = etat?.config.echelle;
  const echelle = useMemo(
    () =>
      echelleConfig?.kind === "fourchette"
        ? { min: echelleConfig.min, max: echelleConfig.max }
        : null,
    [echelleConfig],
  );
  const pyramide = useMemo(
    () => (echelleConfig?.kind === "qsort" ? echelleConfig.pyramide : null),
    [echelleConfig],
  );

  /**
   * Reclassement forcé de la version comparée — la structure de la pyramide du
   * référentiel, qui doit être celle du candidat (§5.1). Portée par la
   * projection ; recalculée pour les versions figées avant son introduction (même
   * fonction pure, donc mêmes niveaux).
   *
   * Une version antérieure garde SON reclassement : c'est ce qui rend un score
   * relu contre v2 identique à ce qu'il était sous v2.
   */
  const niveauxForces = useMemo(() => {
    if (!etat || !version || !pyramide) return undefined;
    return (
      version.niveauxForces ??
      niveauxForcesQsort({
        codes: etat.config.dimensions.map((d) => d.code),
        agregat: version.dimensions as Record<string, StatDimension>,
        pyramide,
      })
    );
  }, [etat, version, pyramide]);

  /**
   * Un classement, quelle que soit l'échelle. Les deux branches produisent la
   * MÊME forme (§5.4), donc tout ce qui suit — jauge, tableau, blocs, sélecteur
   * de version — ne connaît qu'une seule structure.
   *
   * Trié par score décroissant, les candidats sans score exploitable en fin de
   * liste (`-1` ne peut pas être atteint par un score réel, borné à 0-100).
   */
  const classement = useMemo(() => {
    if (!etat || !version) return [];

    const lignes: {
      row: EvaluationPamiRow | EvaluationFitCulturelRow;
      comparaison: ResultatComparaison | null;
      /** Q-sort seulement : conservé pour bâtir le rapport de la sélection. */
      vecteurCandidat?: Record<string, number>;
    }[] = [];

    if (echelle && evaluationsPami) {
      const agregat = version.dimensions as Record<string, StatFourchette>;
      for (const row of evaluationsPami) {
        const scoresCandidat = row.scoresJson
          ? extractScoresFromResult(JSON.parse(row.scoresJson) as TraitResult[])
          : null;
        lignes.push({
          row,
          comparaison: scoresCandidat
            ? comparerFourchette({
                dimensions: etat.config.dimensions,
                scoresCandidat,
                agregat,
                echelle,
                nbReferents: version.nbReferents,
                codesDesaccord: version.dimensionsEnDesaccord,
                // PAMI : référent unique, le seuil de 3 marquerait toujours
                // `faible`.
                seuilReferents: seuilReferentsFiable(etat.config.cible),
              })
            : null,
        });
      }
    }

    if (pyramide && evaluationsFitCulturel) {
      const agregat = version.dimensions as Record<string, StatDimension>;
      for (const row of evaluationsFitCulturel) {
        // Le score est RECALCULÉ depuis le niveau et la pyramide de la campagne :
        // le `score` stocké dans la réponse n'est qu'un confort de relecture.
        const vecteurCandidat = Object.fromEntries(
          row.niveaux.map(({ code, niveau }) => [
            code,
            scoreDuNiveau(niveau, pyramide.length),
          ]),
        );
        lignes.push({
          row,
          vecteurCandidat,
          comparaison: comparerQsort({
            dimensions: etat.config.dimensions,
            vecteurCandidat,
            agregat,
            pyramide,
            nbReferents: version.nbReferents,
            niveauxForces,
          }),
        });
      }
    }

    // Sans score en fin de liste. Pas de valeur plancher en repli : en Q-sort
    // le score est r, et −1 y est un résultat, pas une absence.
    return lignes.sort((a, b) => {
      const sa = a.comparaison?.scoreMatching ?? null;
      const sb = b.comparaison?.scoreMatching ?? null;
      if (sa === null || sb === null) {
        return (sa === null ? 1 : 0) - (sb === null ? 1 : 0);
      }
      return sb - sa;
    });
  }, [
    etat,
    echelle,
    pyramide,
    niveauxForces,
    version,
    evaluationsPami,
    evaluationsFitCulturel,
  ]);

  /**
   * Le tableau n'affiche que le haut du classement : au-delà, la liste devient
   * un mur de lignes sans valeur de lecture. Le candidat sélectionné est
   * toujours joint, même s'il est hors du top, pour que la ligne active reste
   * visible au-dessus de sa restitution.
   */
  const classementAffiche = useMemo(() => {
    if (toutAfficher) return classement;
    const top = classement.slice(0, TOP_CLASSEMENT);
    const selectionnee = classement.find(
      (c) => c.row.parcoursId === parcoursSelectionne,
    );
    return selectionnee && !top.includes(selectionnee)
      ? [...top, selectionnee]
      : top;
  }, [classement, toutAfficher, parcoursSelectionne]);

  const selection =
    classement.find((c) => c.row.parcoursId === parcoursSelectionne) ??
    classement.find((c) => c.comparaison !== null) ??
    classement[0];

  // Rapports du candidat sélectionné (candidat, recruteur…), pour le menu
  // posé dans l'en-tête de sa restitution.
  const rapportsDisponibles = useQuery(
    api.reports.rapportsDisponibles,
    selection && convexUrl ? { evaluationId: selection.row._id } : "skip",
  );

  /**
   * Rapport de matching culturel du candidat sélectionné. Fit-culturel
   * UNIQUEMENT : la corrélation r suppose deux vecteurs ipsatifs comparables
   * item par item, ce qu'une cible en fourchette n'est pas. Le PAMI garde donc
   * la restitution en cible & écart plus bas.
   */
  const rapportCulturel = useMemo(() => {
    if (!etat || !version || !pyramide || !selection?.vecteurCandidat) {
      return null;
    }
    return construireRapportCulturel({
      dimensions: etat.config.dimensions,
      vecteurCandidat: selection.vecteurCandidat,
      agregat: version.dimensions as Record<string, StatDimension>,
      pyramide,
      nbReferents: version.nbReferents,
      niveauxForces,
    });
  }, [etat, version, pyramide, niveauxForces, selection?.vecteurCandidat]);

  /**
   * Lignes prêtes pour le graphique de matching PAMI. Les cibles en score moyen
   * ± dispersion (Q-sort) n'y entrent jamais — cette figure est PAMI only. Une
   * dimension « sans zone » (pas d'attente particulière) y entre quand même : pas d'anneau
   * `[min,max]` à dessiner, mais le score du candidat doit rester visible.
   */
  const lignesMatchingPami: LigneMatchingPami[] = useMemo(
    () =>
      (selection?.comparaison?.parDimension ?? []).flatMap(
        (ligne): LigneMatchingPami[] => {
          if (ligne.cible.kind === "fourchette") {
            return [
              {
                code: ligne.code,
                label: ligne.label,
                valeurCandidat: ligne.valeurCandidat,
                cible: {
                  min: ligne.cible.min,
                  max: ligne.cible.max,
                  poids: ligne.cible.poids,
                },
                ecart: ligne.ecart,
                statut: ligne.statut,
              },
            ];
          }
          if (ligne.cible.kind === "sans-zone") {
            return [
              {
                code: ligne.code,
                label: ligne.label,
                valeurCandidat: ligne.valeurCandidat,
                cible: { sansZone: true as const },
                ecart: ligne.ecart,
                statut: ligne.statut,
              },
            ];
          }
          return [];
        },
      ),
    [selection?.comparaison],
  );

  const passationsEnChargement =
    (echelle !== null && evaluationsPami === undefined) ||
    (pyramide !== null && evaluationsFitCulturel === undefined);

  if (etat === undefined || passationsEnChargement) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (etat === null) {
    return (
      <Card>
        <CardContent className="text-muted-foreground py-8 text-center text-sm">
          Aucun référentiel n'est configuré sur cette campagne. Activez-le depuis
          l'onglet « Référentiel » pour comparer les candidats aux attentes de
          l'organisation.
        </CardContent>
      </Card>
    );
  }

  if (!version) {
    return (
      <Card>
        <CardContent className="text-muted-foreground py-8 text-center text-sm">
          Aucun référent n'a encore contribué : il n'y a pas de référentiel
          contre lequel comparer les candidats.
        </CardContent>
      </Card>
    );
  }

  const estVersionCourante = version.version === versions.length;
  const itemsPersonnalises = etat.config.dimensions.some(
    (d) => d.origine === "custom",
  );
  // Échelle de tracé de la cible : 0-10 en fourchette, scores de niveau en Q-sort
  // (le dernier niveau vaut 1, le premier vaut `nbNiveaux` — cf. `scoreDuNiveau`).
  const echelleTrace = echelle ?? { min: 1, max: pyramide?.length ?? 1 };
  const neutralisees = new Set(version.dimensionsNeutralisees);
  const enDesaccord = new Set(version.dimensionsEnDesaccord);
  const ecartMaxAffiche = Math.max(
    1,
    ...(selection?.comparaison?.parDimension.map((l) => Math.abs(l.ecart)) ?? [
      1,
    ]),
  );

  return (
    <div className="space-y-6">
      {/* Identité de la version — à côté de TOUT score, exports compris (§4.1). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-3 text-base">
            <Compass className="h-4 w-4" />
            Matching au référentiel
            <Badge variant="info">
              v{version.version} · {version.nbReferents} référent
              {version.nbReferents > 1 ? "s" : ""} ·{" "}
              {dateCourte(version.computedAt)}
            </Badge>
            {version.fige && (
              <Badge variant="purple">
                <Lock className="mr-1 h-3 w-3" />
                Figé
              </Badge>
            )}
            {itemsPersonnalises && (
              <Badge variant="outline">Items personnalisés</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {versions.length > 1 && (
            // Plus un <label> : un declencheur Radix n'est pas un controle
            // labelable, le rattachement passe par `aria-label`.
            <div className="flex flex-wrap items-center gap-2">
              <ArrowLeftRight className="text-muted-foreground h-4 w-4" />
              <span>Comparer à la version</span>
              <Select
                value={String(version.version)}
                onValueChange={(valeur) => setVersionChoisie(Number(valeur))}
              >
                <SelectTrigger
                  aria-label="Comparer à la version"
                  className="w-auto min-w-56"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...versions].reverse().map((v) => (
                    <SelectItem key={v.version} value={String(v.version)}>
                      v{v.version} — {v.nbReferents} référent
                      {v.nbReferents > 1 ? "s" : ""} · {dateCourte(v.computedAt)}
                      {v.version === versions.length ? " (courante)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!estVersionCourante && (
            <div className="flex gap-2 rounded-surface border border-etat-alerte-trait bg-etat-alerte-surface p-3 text-etat-alerte-contenu">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <p>
                Vous lisez une version antérieure du référentiel. Les scores
                affichés ne sont pas ceux de la version courante (v
                {versions.length}).
              </p>
            </div>
          )}

          {!version.fige && (
            <p className="text-muted-foreground text-xs">
              Le référentiel est encore ouvert : une contribution tardive fera
              bouger tous les scores en même temps. Figez-le avant d'arbitrer.
            </p>
          )}

          {itemsPersonnalises && (
            <p className="text-muted-foreground text-xs">
              Ce référentiel comporte des items personnalisés : les scores ne
              sont comparables qu'entre candidats de cette campagne.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Classement — INTRA-campagne uniquement (§11.2). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Classement des candidats{" "}
            {toutAfficher || classement.length <= TOP_CLASSEMENT
              ? `(${classement.length})`
              : `(${TOP_CLASSEMENT} meilleurs sur ${classement.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {classement.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              Aucune évaluation PAMI dans cette campagne.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      #
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      Candidat
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      {echelle ? "Score de matching" : "Corrélation (r)"}
                    </th>
                    {/* Pas de colonne d'interprétation en PAMI : le score y est
                        la moyenne pondérée des dimensions atteintes, il se
                        lit directement. En fit-culturel, c'est la zone du
                        rapport — un r non significatif se lit « zone neutre ». */}
                    {!echelle && (
                      <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                        Interprétation
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {classementAffiche.map((entree) => {
                    const index = classement.indexOf(entree);
                    const score = entree.comparaison?.scoreMatching ?? null;
                    const indicateur = echelle
                      ? null
                      : interpreterCorrelation(
                          entree.comparaison?.correlation ?? null,
                        );
                    const actif =
                      selection?.row.parcoursId === entree.row.parcoursId;
                    return (
                      <tr
                        key={entree.row._id}
                        onClick={() =>
                          setParcoursSelectionne(entree.row.parcoursId)
                        }
                        // La ligne active porte un violet pâle plutôt qu'un gris :
                        // la restitution affichée dessous est celle de CE
                        // candidat, la sélection doit se repérer sans la chercher.
                        className={`cursor-pointer border-b last:border-0 ${
                          actif
                            ? "bg-accent hover:bg-accent"
                            : "hover:bg-muted/40"
                        }`}
                      >
                        <td className="text-muted-foreground px-4 py-2 tabular-nums">
                          {score === null ? "—" : index + 1}
                        </td>
                        <td className="px-4 py-2 font-medium">
                          {entree.row.prenomCandidat} {entree.row.nomCandidat}
                        </td>
                        <td className="px-4 py-2">
                          {score === null ? (
                            <span className="text-muted-foreground">
                              {echelle
                                ? "PAMI non exploitable"
                                : "Passation incomplète"}
                            </span>
                          ) : indicateur ? (
                            <div className="flex items-center gap-2">
                              <span className="w-12 tabular-nums">
                                {formatR(score)}
                              </span>
                              {/* Axe [-1, 1] centré sur 0 (le hasard) : la
                                  barre part du centre vers r, comme l'axe du
                                  rapport. */}
                              <div className="relative h-2 w-24 overflow-hidden rounded-full bg-muted">
                                <div className="absolute inset-y-0 left-1/2 w-px bg-foreground/30" />
                                <div
                                  className="absolute inset-y-0 rounded-full"
                                  style={{
                                    left: `${50 + Math.min(0, score) * 50}%`,
                                    width: `${Math.max(1, Math.abs(score) * 50)}%`,
                                    backgroundColor:
                                      COULEUR_ZONE[indicateur.zone],
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="w-8 tabular-nums">
                                {Math.round(score)}
                              </span>
                              <div className="relative h-2 w-24 overflow-hidden rounded-full bg-muted">
                                <div
                                  className="h-full rounded-full bg-primary"
                                  style={{
                                    width: `${Math.max(1, Math.min(100, score))}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        {indicateur && (
                          <td className="text-muted-foreground px-4 py-2">
                            {score === null
                              ? "—"
                              : libelleIndicateur(indicateur)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {classement.length > TOP_CLASSEMENT && (
                <div className="border-t p-3 text-center">
                  <button
                    type="button"
                    onClick={() => setToutAfficher((v) => !v)}
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    {toutAfficher
                      ? `Afficher seulement les ${TOP_CLASSEMENT} meilleurs`
                      : `Afficher les ${classement.length} candidats`}
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restitution du candidat sélectionné. En fit-culturel c'est le rapport
          de matching culturel complet ; en PAMI la lecture en cible & écart. */}
      {rapportCulturel && selection ? (
        <RapportMatchingCulturel
          rapport={rapportCulturel}
          candidat={`${selection.row.prenomCandidat ?? ""} ${
            selection.row.nomCandidat ?? ""
          }`.trim()}
          campagneNom={campagneNom}
          datePassation={
            "dateFin" in selection.row ? selection.row.dateFin : undefined
          }
          reference={{
            version: version.version,
            computedAt: version.computedAt,
            nbReferents: version.nbReferents,
          }}
          rapportPdf={
            convexUrl
              ? {
                  evaluationId: selection.row._id,
                  prenom: selection.row.prenomCandidat,
                  nom: selection.row.nomCandidat,
                  rapports: rapportsDisponibles,
                  token: token ?? null,
                  convexUrl,
                }
              : undefined
          }
          rang={
            selection.comparaison?.scoreMatching === null ||
            selection.comparaison === null
              ? undefined
              : {
                  position:
                    classement.findIndex(
                      (c) => c.row.parcoursId === selection.row.parcoursId,
                    ) + 1,
                  total: classement.length,
                }
          }
        />
      ) : selection?.comparaison ? (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex flex-wrap items-center gap-3 text-base">
                {selection.row.prenomCandidat} {selection.row.nomCandidat}
                <Badge variant="info">
                  v{version.version} · {version.nbReferents} référent
                  {version.nbReferents > 1 ? "s" : ""} ·{" "}
                  {dateCourte(version.computedAt)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {selection.comparaison.scoreMatching === null ? (
                <p className="text-sm">
                  {echelle
                    ? "Score non défini : aucune dimension de ce référentiel ne discrimine. Le référent doit se positionner sur au moins une dimension pour qu'un score soit calculable."
                    : "Score non défini : aucune dimension classée par les référents ne se retrouve dans la passation de ce candidat."}
                </p>
              ) : (
                <JaugeMatching
                  score={selection.comparaison.scoreMatching}
                  fiabilite={selection.comparaison.fiabilite}
                  rang={
                    classement.findIndex(
                      (c) => c.row.parcoursId === selection.row.parcoursId,
                    ) + 1
                  }
                  total={classement.length}
                />
              )}

              {/* PAMI : une SEULE figure remplace le détail ligne à ligne —
                  les zones du référentiel en anneaux polaires, le profil du
                  candidat en radar par-dessus. Le tableau reste la restitution
                  de repli du fit culturel, dont la cible est un score entouré
                  d'un cran de pyramide et non une plage saisie. */}
              {echelle ? (
                <PamiMatchingChart
                  lignes={lignesMatchingPami}
                  echelle={echelle}
                />
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/30 border-b">
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Dimension
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Cible &amp; candidat
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Candidat
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Écart
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selection.comparaison.parDimension.map((ligne) => (
                      <tr key={ligne.code} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className="font-medium">{ligne.label}</span>
                            {ligne.categorie === "personnalite" && (
                              <span className="text-muted-foreground text-xs">
                                personnalité
                              </span>
                            )}
                            {/* En PAMI, toutes les zones à attente unique sont
                                étroites : le badge serait sur chaque ligne. */}
                            {ligne.exigenceForte &&
                              ligne.cible.kind !== "fourchette" && (
                                <Badge variant="outline">exigence forte</Badge>
                              )}
                            {neutralisees.has(ligne.code) && (
                              <span className="text-muted-foreground text-xs">
                                hors calcul
                              </span>
                            )}
                            {enDesaccord.has(ligne.code) && (
                              <span className="text-muted-foreground text-xs">
                                référents en désaccord
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <TrackCible
                            bande={bandeCible(ligne)}
                            valeurCandidat={ligne.valeurCandidat}
                            echelle={echelleTrace}
                            statut={ligne.statut}
                          />
                          <span className="text-muted-foreground text-xs">
                            {ligne.cible.kind === "fourchette"
                              ? `cible ${arrondi1(ligne.cible.min)}–${arrondi1(ligne.cible.max)}`
                              : ligne.cible.kind === "score"
                                ? `attendu ${arrondi1(ligne.cible.valeur)} ± ${arrondi1(
                                    Math.max(1, ligne.cible.dispersion),
                                  )}`
                                : "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {arrondi1(ligne.valeurCandidat)}
                        </td>
                        <td className="px-3 py-2">
                          <BarreEcart
                            ligne={ligne}
                            ecartMaxAffiche={ecartMaxAffiche}
                          />
                          <span className="text-muted-foreground text-xs tabular-nums">
                            {ligne.ecart === 0
                              ? "0"
                              : `${arrondi1(Math.abs(ligne.ecart))} pt${
                                  Math.abs(ligne.ecart) > 1 ? "s" : ""
                                }`}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <span
                              className="h-2 w-2 shrink-0 rounded-full"
                              style={{
                                backgroundColor: COULEUR_STATUT[ligne.statut],
                              }}
                            />
                            {LIBELLE_STATUT[ligne.statut]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </CardContent>
          </Card>

          {!echelle && (
          <div className="grid gap-4 md:grid-cols-3">
            <BlocDimensions
              titre="Points forts"
              description="Dans la cible, sur des dimensions qui pèsent — zone attendue en PAMI, attente tranchée en PC Fit."
              icone={<CheckCircle2 className="h-4 w-4" />}
              lignes={selection.comparaison.pointsForts}
            />
            <BlocDimensions
              titre="Points de vigilance"
              description="Sous la cible sur une dimension qui pèse fortement."
              icone={<AlertTriangle className="h-4 w-4" />}
              lignes={selection.comparaison.pointsVigilance}
              noteHexaco
            />
            <BlocDimensions
              titre="Angles morts"
              description="Au-dessus de la cible : des attentes du candidat que le poste ne sert pas."
              icone={<Search className="h-4 w-4" />}
              lignes={selection.comparaison.anglesMorts}
              noteHexaco
            />
          </div>
          )}
        </>
      ) : null}
    </div>
  );
}
